import { useEffect, useCallback } from 'react';
import { shouldPersistTask } from '@/utils/taskUtils';
import { Task } from '@/types/tasks';
import { makeLogger } from '@/utils/logger/logger.ts';

const logger = makeLogger('useTaskPersistence');

const TASK_CACHE_KEY = 'tasks';

/**
 * Custom hook for managing task persistence to localStorage
 * Encapsulates localStorage logic following PRINCIPLE 5
 */
export const useTaskPersistence = (tasks: Task[]) => {
	// Load tasks from localStorage with optimized filtering
	const loadTasksFromStorage = useCallback((): Task[] => {
		// Corrupt/partial/schema-skewed cache must not crash task init — fall back to empty.
		let parsedTasks: Task[] = [];
		try {
			const savedTasks = localStorage.getItem(TASK_CACHE_KEY);
			const parsed = savedTasks ? JSON.parse(savedTasks) : [];
			if (Array.isArray(parsed)) parsedTasks = parsed;
		} catch {
			parsedTasks = [];
		}
		const currentTime = Date.now();

		const filteredTasks = parsedTasks.filter((task: Task) => {
			// Remove expired tasks
			if (task.expiresAt && task.expiresAt <= currentTime) {
				logger.debug(
					`Removing expired task: ${task.id} (${task.type}) - expired at ${new Date(task.expiresAt).toISOString()}`,
				);
				return false;
			}

			// Use the utility function for consistent persistence logic
			const shouldKeep = shouldPersistTask(task);
			if (!shouldKeep) {
				logger.debug(`Removing old task: ${task.id} (${task.type}, ${task.status})`);
			}
			return shouldKeep;
		});

		if (parsedTasks.length !== filteredTasks.length) {
			logger.debug(`localStorage cleanup: loaded ${filteredTasks.length}/${parsedTasks.length} tasks`);
		}

		return filteredTasks;
	}, []);

	// Persist tasks to localStorage - only for actual side effects (PRINCIPLE 2)
	useEffect(() => {
		const tasksToStore = tasks.filter(shouldPersistTask);
		localStorage.setItem(TASK_CACHE_KEY, JSON.stringify(tasksToStore));

		// Log storage optimization info
		if (tasks.length !== tasksToStore.length) {
			logger.debug(`localStorage optimization: storing ${tasksToStore.length}/${tasks.length} tasks`);
		}
	}, [tasks]);

	// Clear tasks from localStorage
	const clearTasksFromStorage = useCallback(() => {
		localStorage.removeItem(TASK_CACHE_KEY);
	}, []);

	return {
		loadTasksFromStorage,
		clearTasksFromStorage,
	};
};
